import {
  alpha,
  Button,
  ButtonProps,
  CircularProgress,
  useTheme,
} from "@mui/material";
import { ReactNode } from "react";

export interface ActionButtonProps extends Omit<
  ButtonProps,
  "variant" | "color"
> {
  /** Button text content */
  children: ReactNode;
  /** Shows loading spinner and disables button */
  loading?: boolean;
  /** Loading text to display (defaults to children if not provided) */
  loadingText?: string;
}

/**
 * Premium action button with gradient background, shadow effects,
 * hover lift animation, and shimmer effect.
 *
 * Used for primary actions across Nemo Docs, Nemo Audio, and Translation pages.
 */
export const ActionButton = ({
  children,
  loading = false,
  loadingText,
  disabled,
  sx,
  startIcon,
  ...props
}: ActionButtonProps) => {
  const theme = useTheme();
  const isDisabled = disabled || loading;

  return (
    <Button
      variant="contained"
      disabled={isDisabled}
      startIcon={
        loading ? <CircularProgress size={20} color="inherit" /> : startIcon
      }
      sx={{
        minHeight: "44px",
        borderRadius: "16px",
        textTransform: "none",
        fontSize: "16px",
        fontWeight: 600,
        padding: "14px 20px",
        letterSpacing: "-0.2px",
        position: "relative",
        overflow: "hidden",
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        boxShadow: `0 8px 20px ${alpha(theme.palette.primary.main, 0.3)}, 0 2px 8px ${alpha(theme.palette.primary.main, 0.2)}`,
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        "&:hover": {
          background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.dark} 100%)`,
          transform: "translateY(-3px)",
          boxShadow: `0 12px 28px ${alpha(theme.palette.primary.main, 0.4)}, 0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
        },
        "&:active": {
          transform: "translateY(-1px)",
        },
        "&:disabled": {
          background: "linear-gradient(135deg, #c5c5c5 0%, #a8a8a8 100%)",
          boxShadow: "none",
          transform: "none",
        },
        // Shimmer effect on hover
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: "-100%",
          width: "100%",
          height: "100%",
          background:
            "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)",
          transition: "left 0.5s ease",
        },
        "&:hover::before": {
          left: "100%",
        },
        ...sx,
      }}
      {...props}
    >
      {loading && loadingText ? loadingText : children}
    </Button>
  );
};
